import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import { FC, createContext, useContext, useMemo, useState } from 'react';
import {
  StockLocationPurpose,
  StockLocationSchema,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import _ from 'lodash';
import { useScannerContext } from 'contexts/ScannerContext';
import { ALL, DISABLE_PAGE_LIMIT, QueryFilterOperators } from 'types/api';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  useGetStockLocationsClientV2Query,
  useUpdateStockLocationClientV2Mutation,
} from 'store/slices/clientV2/stockLocations';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { StockLocationType } from 'types/inventory';
import useSnackbar from 'hooks/useSnackbar';
import MobileLoader from 'ui-component/mobile/MobileLoader';
import { KitRequestSchema } from 'types/kitting';
import { ShipmentSchema } from 'types/clientV2/shipments';
import { Org } from 'types/org';

type WorkstationFilter = {
  purpose?: StockLocationPurpose | null;
  org?: Org['id'] | null;
  shipment?: ShipmentSchema['id'] | null;
  kitRequest?: KitRequestSchema['id'] | null;
};

type WarehouseWorkstationContextProps = {
  workstationLocations: StockLocationSchemaLite[];
  workstationLocationsById: Record<string, StockLocationSchemaLite>;
  isFetchingWorkstationLocations: boolean;
  targetLocation: StockLocationSchemaLite | null;
  selectedLocation: StockLocationSchemaLite | null;
  setSelectedLocation: (location: StockLocationSchemaLite | null) => void;
  locationFilters: WorkstationFilter;
  setLocationFilters: (filters: WorkstationFilter) => void;
  filteredWorkstationLocations: StockLocationSchemaLite[];
};

const WarehouseWorkstationContext = createContext<
  WarehouseWorkstationContextProps | undefined
>(undefined);

export const useWarehouseWorkstationContext = () => {
  const context = useContext(WarehouseWorkstationContext);
  if (!context) {
    throw new Error(
      'useWarehouseWorkstationContext must be used within a WarehouseWorkstationProvider'
    );
  }
  return context;
};

type WarehouseWorkstationProviderProps = {
  children: React.ReactNode;
};

export const WarehouseWorkstationProvider: FC<
  WarehouseWorkstationProviderProps
> = ({ children }) => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const [targetLocation, setTargetLocation] =
    useState<StockLocationSchemaLite | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<StockLocationSchemaLite | null>(null);
  const [updateStockLocation, { isLoading: isUpdatingStockLocation }] =
    useUpdateStockLocationClientV2Mutation();
  const [locationFilters, setLocationFilters] = useState<WorkstationFilter>({});

  const {
    data: workstationLocationsResponse,
    isFetching: isFetchingWorkstationLocations,
  } = useServerSideQuery<StockLocationSchema, StockLocationSchemaLite>(
    useGetStockLocationsClientV2Query,
    // @ts-ignore -- Nested filter
    settings?.currentWorkstation?.id
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [ALL],
          filters: [
            {
              field: 'parent',
              operator: QueryFilterOperators['='],
              value: settings.currentWorkstation.id,
            },
            'or',
            {
              field: 'parent.parent',
              operator: QueryFilterOperators['='],
              value: settings.currentWorkstation.id,
            },
          ],
        }
      : skipToken
  );

  const workstationLocationsById = useMemo(() => {
    if (!workstationLocationsResponse?.data) return {};
    return workstationLocationsResponse.data.reduce<
      Record<string, StockLocationSchemaLite>
    >((acc, location) => {
      acc[location.id] = location;
      return acc;
    }, {});
  }, [workstationLocationsResponse?.data]);

  const filteredWorkstationLocations = useMemo(
    () =>
      (workstationLocationsResponse?.data || []).filter((location) =>
        _.every(locationFilters, (value, key) => _.get(location, key) === value)
      ),
    [workstationLocationsResponse?.data, locationFilters]
  );

  const handleScannedStockLocation = async (
    scannedStockLocation: StockLocationSchemaLite
  ) => {
    // move scanned location to this workstation if it's a bin and not already in this workstation
    if (scannedStockLocation.locationType === StockLocationType.ESD_BIN) {
      try {
        await updateStockLocation({
          id: scannedStockLocation.id,
          parent: settings.currentWorkstation.id,
        });
        dispatchSuccessSnackbar(
          `Moved ${scannedStockLocation.name} → ${settings.currentWorkstation.name}`
        );
      } catch (error) {
        dispatchErrorSnackbar('Error: Unable to move bin.');
      }
    }

    // check if location is a bin section that is not in a bin in this workstation and move the bin if it is
    if (
      scannedStockLocation.locationType === StockLocationType.BIN_SECTION &&
      !_.find(
        workstationLocationsResponse?.data || [],
        (sl) => sl.id === scannedStockLocation.parent
      )
    ) {
      try {
        await updateStockLocation({
          id: scannedStockLocation.parent || '',
          parent: settings.currentWorkstation.id,
        });
        dispatchSuccessSnackbar(
          `Moved ${
            scannedStockLocation.name.split('-')[0] || scannedStockLocation.name
          } → ${settings.currentWorkstation.name}`
        );
      } catch (error) {
        dispatchErrorSnackbar('Error: Unable to move bin.');
      }
    }

    if (scannedStockLocation.locationType === StockLocationType.BIN_SECTION) {
      setTargetLocation(scannedStockLocation);
    }
  };

  useScannerContext({
    handleScannedStockLocation,
  });

  return (
    <WarehouseWorkstationContext.Provider
      value={{
        workstationLocations: workstationLocationsResponse?.data || [],
        workstationLocationsById,
        isFetchingWorkstationLocations,
        targetLocation,
        selectedLocation,
        setSelectedLocation,
        locationFilters,
        setLocationFilters,
        filteredWorkstationLocations,
      }}
    >
      {children}
      {(isUpdatingStockLocation || isFetchingWorkstationLocations) && (
        <MobileLoader />
      )}
    </WarehouseWorkstationContext.Provider>
  );
};
